import React, { type ReactElement, type ReactNode, useEffect, useState } from 'react'

import { numberToDecimalString } from 'commons/utils/number'
import { Modal, Tooltip } from 'components'
import { clearTabBlockers, setDirty } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

export type TabsListChildren = {
  initialTab?: number
  children: ReactElement<ListTabProps> | ReactElement<ListTabProps>[]
  showTotalizer?: boolean
  onClick?: (label: string) => void
  controlledTab?: number
  setControlledTab?: (tab: number) => void
}

export type ListTabProps = {
  label: string
  tooltip?: NonNullable<React.ReactNode>
  hidden?: boolean
  disabled?: boolean
  totalizer?: number
  children?: ReactNode | ReactNode[]
}

export const TabsList = ({
  children,
  initialTab = 0,
  showTotalizer = false,
  onClick,
  controlledTab = -1,
  setControlledTab,
}: TabsListChildren) => {
  const dispatch = useDispatch()

  const [activeTab, setActiveTab] = useState<number>(initialTab)
  const [selectedTab, setSelectedTab] = useState<number>(initialTab)
  const [blockedTabModal, setBlockedTabModal] = useState<boolean>(false)
  const [tabLabels, setTabLabels] = useState<string[]>([])
  const [tabTotalizer, setTabTotalizer] = useState<number[]>([])
  const [hiddenTabs, setHiddenTabs] = useState<boolean[]>([])
  const [disabledTabs, setDisabledTabs] = useState<boolean[]>([])
  const [childrenContents, setChildrenContents] = useState<ReactNode[]>([])
  const [tooltips, setTooltips] = useState<ReactNode[]>([])

  const { tabBlockers } = useSelector(({ structure }) => structure)

  useEffect(() => {
    const labels: string[] = []
    const totalizer: number[] = []
    const contents: ReactNode[] = []
    const tooltip: ReactNode[] = []
    const hiddenTabsMap: boolean[] = []
    const disabledTabsMap: boolean[] = []

    React.Children.forEach(children, (child, index) => {
      labels[index] = child.props.label
      contents[index] = child.props.children
      tooltip[index] = child.props.tooltip
      totalizer[index] = child.props.totalizer ?? 0
      hiddenTabsMap[index] = child.props.hidden || false
      disabledTabsMap[index] = child.props.disabled || false
    })
    setTooltips(tooltip)
    setTabLabels(labels)
    setHiddenTabs(hiddenTabsMap)
    setDisabledTabs(disabledTabsMap)
    setChildrenContents(contents)
    setTabTotalizer(totalizer)
  }, [children])

  useEffect(() => {
    if (controlledTab > -1) setActiveTab(controlledTab)
  }, [controlledTab])

  const handleTabChange = (index: number) => {
    setSelectedTab(index)
    if (onClick) onClick(tabLabels[index])
    if (tabBlockers.length > 0) {
      setBlockedTabModal(true)
    } else {
      setActiveTab(index)
      if (setControlledTab) setControlledTab(index)
    }
  }

  const handleUnblockTab = () => {
    setBlockedTabModal(false)
    dispatch(clearTabBlockers())
    dispatch(setDirty(false))
    setActiveTab(selectedTab)
    if (setControlledTab) setControlledTab(selectedTab)
  }

  return (
    <>
      <S.Wrapper>
        <S.TabsCol>
          {tabLabels.map((label, index) => {
            if (!hiddenTabs[index])
              return (
                <Tooltip arrow key={label} placement="top-end" title={tooltips[index] ?? ''}>
                  <S.TabItem
                    active={index === activeTab}
                    disabled={disabledTabs[index]}
                    onClick={
                      disabledTabs[index]
                        ? () => {
                            return
                          }
                        : () => handleTabChange(index)
                    }
                  >
                    <S.TabTitle>
                      {label}{' '}
                      {showTotalizer && (
                        <S.TabTotalizer>
                          {numberToDecimalString(tabTotalizer[index])}
                        </S.TabTotalizer>
                      )}
                    </S.TabTitle>
                  </S.TabItem>
                </Tooltip>
              )
          })}
        </S.TabsCol>

        {Object.keys(childrenContents).map((key, index) => {
          if (index === activeTab) {
            return (
              <S.ContentContainer key={`contents-${key}`}>
                {childrenContents[activeTab]}
              </S.ContentContainer>
            )
          }
        })}
      </S.Wrapper>

      <Modal
        buttonAction={() => setBlockedTabModal(false)}
        buttonText="Continuar a edição"
        description="Existem campos que não foram salvos, deseja prosseguir mesmo assim?"
        open={blockedTabModal}
        returnAction={handleUnblockTab}
        returnIcon={<></>}
        returnText="Descartar o progresso"
        setOpen={(open: boolean) => setBlockedTabModal(open)}
        showReturn
        title="Continuar de onde parou"
      />
    </>
  )
}

const Tab = ({ label, children }: ListTabProps) => <div key={label}>{children}</div>
TabsList.Tab = Tab
